import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Search, Filter, Plus, X,
  User, Users, Heart, UserPlus, Briefcase,
  CreditCard, Building2, Pencil, Trash2,
  ArrowUpAZ, ArrowDownAZ, Loader2,
  Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { 
  subscribeToConnections, 
  addConnection, 
  updateConnection, 
  deleteConnection 
} from "@/services/connectionsService";

// Color palette for cards and banks
const colorPalette = [
  { name: "Rosa", hex: "#ec4899", bg: "bg-pink-500" },
  { name: "Roxo", hex: "#8b5cf6", bg: "bg-violet-500" },
  { name: "Índigo", hex: "#6366f1", bg: "bg-indigo-500" },
  { name: "Azul", hex: "#3b82f6", bg: "bg-blue-500" },
  { name: "Ciano", hex: "#06b6d4", bg: "bg-cyan-500" },
  { name: "Verde", hex: "#10b981", bg: "bg-emerald-500" },
  { name: "Lima", hex: "#84cc16", bg: "bg-lime-500" },
  { name: "Amarelo", hex: "#eab308", bg: "bg-yellow-500" },
  { name: "Laranja", hex: "#f97316", bg: "bg-orange-500" },
  { name: "Vermelho", hex: "#ef4444", bg: "bg-red-500" },
  { name: "Cinza", hex: "#6b7280", bg: "bg-gray-500" },
  { name: "Slate", hex: "#64748b", bg: "bg-slate-500" },
];

// Relationship types
const relationshipTypes = [
  { id: "couple", label: "Casal", icon: Heart },
  { id: "family", label: "Família", icon: Users },
  { id: "friends", label: "Amigos", icon: UserPlus },
  { id: "other", label: "Outros", icon: Briefcase },
];

// Filter pills
const filterOptions = [
  { id: "all", label: "Todos" },
  { id: "people", label: "Pessoas" },
  { id: "cards", label: "Cartões" },
  { id: "banks", label: "Bancos" },
  { id: "categories", label: "Categorias" },
];

export default function Connections() {
  const { user } = useAuth();
  
  // State
  const [connections, setConnections] = useState({ people: [], cards: [], banks: [], categories: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortAsc, setSortAsc] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categoryForm, setCategoryForm] = useState({ name: "", categoryType: "expense" });
  
  // Modal states
  const [showPersonModal, setShowPersonModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);
  const [showFAB, setShowFAB] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form states
  const [personForm, setPersonForm] = useState({
    name: "", nickname: "", relationship: "", relationshipCustom: ""
  });
  const [cardForm, setCardForm] = useState({
    name: "", limit: "", dueDay: "", closeDay: "", color: "#6366f1"
  });
  const [bankForm, setBankForm] = useState({
    name: "", color: "#10b981"
  });

  // Subscribe to connections from Firestore
  useEffect(() => {
    if (!user) return;
    
    setIsLoading(true);
    const unsubscribe = subscribeToConnections(user.uid, (data) => {
      setConnections(data);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Get all items combined
  const getAllItems = useCallback(() => {
    const { people, cards, banks } = connections;
    let items = [];
    
    if (activeFilter === "all" || activeFilter === "people") {
      items = [...items, ...people];
    }
    if (activeFilter === "all" || activeFilter === "cards") {
      items = [...items, ...cards];
    }
    if (activeFilter === "all" || activeFilter === "banks") {
      items = [...items, ...banks];
    }
    if (activeFilter === "all" || activeFilter === "categories") {
      items = [...items, ...(connections.categories || [])];
    }
    
    return items;
  }, [connections, activeFilter]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let items = getAllItems();
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        (item.nickname && item.nickname.toLowerCase().includes(query))
      );
    }
    
    // Sort: Primeiro por Tipo (para agrupar), depois por Nome
    items.sort((a, b) => {
      if (activeFilter === "all" && a.type !== b.type) {
        const typeOrder = { person: 1, card: 2, bank: 3, category: 4 };
        return typeOrder[a.type] - typeOrder[b.type];
      }
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
    
    return items;
  }, [getAllItems, searchQuery, sortAsc]);

  // Reset forms
  const resetForms = () => {
    setPersonForm({ name: "", nickname: "", relationship: "", relationshipCustom: "" });
    setCardForm({ name: "", limit: "", dueDay: "", closeDay: "", color: "#6366f1" });
    setBankForm({ name: "", color: "#10b981" });
    setCategoryForm({ name: "", categoryType: "expense" });
    setEditingItem(null);
  };

  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
    if (item.type === "person") {
      setPersonForm({
        name: item.name,
        nickname: item.nickname || "",
        relationship: item.relationship,
        relationshipCustom: item.relationshipCustom || ""
      });
      setShowPersonModal(true);
    } else if (item.type === "card") {
      setCardForm({
        name: item.name,
        limit: item.limit.toString(),
        dueDay: item.dueDay.toString(),
        closeDay: item.closeDay.toString(),
        color: item.color
      });
      setShowCardModal(true);
    } else if (item.type === "bank") {
      setBankForm({
        name: item.name,
        color: item.color
      });
      setShowBankModal(true);
    }
    else if (item.type === "category") {
  setCategoryForm({
    name: item.name,
    categoryType: item.categoryType || "expense"
  });
  setShowCategoryModal(true);
}
  };

  // Handle delete
  const handleDelete = (item) => {
    setDeleteConfirm(item);
  };

const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    setIsSaving(true);
    try {
      // Mensagens ácidas e informativas para cada tipo de exclusão
      const messages = {
        category: `Lançamentos de "${deleteConfirm.name}" agora aparecerão como "Outros".`,
        person: `"${deleteConfirm.name}" foi removido, mas o histórico de quem deve a quem continua salvo.`,
        card: `O cartão "${deleteConfirm.name}" sumiu, mas as dívidas dele ainda aparecem nos relatórios.`,
        bank: `O banco "${deleteConfirm.name}" foi desconectado. O rastro do dinheiro permanece.`
      };

      toast.info(messages[deleteConfirm.type] || "Item removido do backstage.", {
        duration: 6000,
      });

      await deleteConnection(deleteConfirm.id);
      toast.success("Excluído com sucesso!");
    } catch (error) {
      toast.error("Erro ao excluir. O item lutou para ficar, tente de novo.");
    }
    setIsSaving(false);
    setDeleteConfirm(null);
  };

  // Save Person
  const savePerson = async () => {
    if (!personForm.name.trim()) {
      toast.error("Nome é obrigatório!");
      return;
    }
    if (!personForm.relationship) {
      toast.error("Selecione o relacionamento!");
      return;
    }
    if (personForm.relationship === "other" && !personForm.relationshipCustom.trim()) {
      toast.error("Especifique o tipo de relacionamento!");
      return;
    }

    setIsSaving(true);
    try {
      const personData = {
        ...personForm,
        type: "person"
      };

      if (editingItem) {
        await updateConnection(editingItem.id, personData);
        toast.success("Pessoa atualizada!");
      } else {
        await addConnection(user.uid, personData);
        toast.success("Pessoa adicionada!");
      }
      setShowPersonModal(false);
      resetForms();
    } catch (error) {
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setIsSaving(false);
  };
// Save Categoria
  const saveCategory = async () => {
  if (!categoryForm.name.trim()) return toast.error("Nome obrigatório!");
  setIsSaving(true);
  try {
    const data = { ...categoryForm, type: "category" };
    if (editingItem) {
      await updateConnection(editingItem.id, data);
      toast.success("Categoria atualizada!");
    } else {
      await addConnection(user.uid, data);
      toast.success("Categoria adicionada!");
    }
    setShowCategoryModal(false);
    resetForms();
  } catch (error) {
    toast.error("Erro ao salvar.");
  }
  setIsSaving(false);
};

  // Save Card
  const saveCard = async () => {
    if (!cardForm.name.trim()) {
      toast.error("Nome do cartão é obrigatório!");
      return;
    }
if (!cardForm.dueDay || !cardForm.closeDay) {
      toast.error("Preencha o fechamento e o vencimento!");
      return;
    }

    setIsSaving(true);
    try {
      const cardData = {
        name: cardForm.name,
        limit: cardForm.limit ? parseFloat(cardForm.limit) : 0,
        dueDay: parseInt(cardForm.dueDay),
        closeDay: parseInt(cardForm.closeDay),
        color: cardForm.color,
        type: "card"
      };

      if (editingItem) {
        await updateConnection(editingItem.id, cardData);
        toast.success("Cartão atualizado!");
      } else {
        await addConnection(user.uid, cardData);
        toast.success("Cartão adicionado!");
      }
      setShowCardModal(false);
      resetForms();
    } catch (error) {
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setIsSaving(false);
  };

  // Save Bank
  const saveBank = async () => {
    if (!bankForm.name.trim()) {
      toast.error("Nome do banco é obrigatório!");
      return;
    }

    setIsSaving(true);
    try {
      const bankData = {
        name: bankForm.name,
        color: bankForm.color,
        type: "bank"
      };

      if (editingItem) {
        await updateConnection(editingItem.id, bankData);
        toast.success("Banco atualizado!");
      } else {
        await addConnection(user.uid, bankData);
        toast.success("Banco adicionado!");
      }
      setShowBankModal(false);
      resetForms();
    } catch (error) {
      toast.error("Erro ao salvar. Tente novamente.");
    }
    setIsSaving(false);
  };

  // Get relationship icon
  const getRelationshipIcon = (relationship) => {
    const rel = relationshipTypes.find(r => r.id === relationship);
    return rel ? rel.icon : User;
  };

  // Get relationship label
  const getRelationshipLabel = (item) => {
    if (item.relationship === "other" && item.relationshipCustom) {
      return item.relationshipCustom;
    }
    const rel = relationshipTypes.find(r => r.id === item.relationship);
    return rel ? rel.label : "";
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="max-w-lg mx-auto px-6 py-8">
        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground mb-1">
            Conexões
          </h1>
          <p className="text-muted-foreground text-sm">
            Gerencie pessoas, cartões e bancos em um só lugar
          </p>
        </motion.header>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative mb-4"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou apelido..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 rounded-xl bg-white border-slate-200"
            data-testid="search-connections"
          />
        </motion.div>

        {/* Filter Pills & Sort */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex items-center justify-between gap-3 mb-6"
        >
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 md:flex-wrap md:justify-start">
      {filterOptions.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                data-testid={`filter-${filter.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilter === filter.id
                    ? "bg-primary text-white shadow-md shadow-primary/30"
                    : "bg-white text-muted-foreground hover:bg-slate-100 border border-slate-200"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSortAsc(!sortAsc)}
            className="rounded-xl bg-white border border-slate-200 flex-shrink-0"
            data-testid="sort-toggle"
          >
            {sortAsc ? (
              <ArrowUpAZ className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ArrowDownAZ className="w-5 h-5 text-muted-foreground" />
            )}
          </Button>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">Carregando conexões...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredItems.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-16 text-center"
          >
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
              <Users className="w-10 h-10 text-slate-400" />
            </div>
            <h3 className="font-bold text-foreground mb-2">
              {searchQuery ? "Nenhum resultado encontrado" : "Nenhuma conexão ainda"}
            </h3>
            <p className="text-muted-foreground text-sm max-w-xs">
                {searchQuery 
                  ? "O Troquinho procurou até embaixo do sofá, mas não achou ninguém com esse nome."
                  : "Sua vida financeira está um deserto. Que tal adicionar alguém para dividir as dívidas (ou um cartão para criá-las)?"}
              </p>
          </motion.div>
        )}

        {/* Botão de Adição Contextual (Apenas para Categorias) */}
{!isLoading && activeFilter === "categories" && (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-4"
  >
    <Button
      onClick={() => setShowCategoryModal(true)}
      variant="outline"
      className="w-full h-12 rounded-xl border-dashed border-2 border-slate-300 text-slate-500 hover:text-primary hover:border-primary transition-all flex items-center justify-center gap-2"
    >
      <Plus className="w-5 h-5" />
      Adicionar Nova Categoria
    </Button>
  </motion.div>
)}

        {/* Items List */}
        {!isLoading && filteredItems.length > 0 && (
          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {(() => { 
              let lastType = null; 
              return filteredItems.map((item, index) => {
                const showHeader = activeFilter === "all" && item.type !== lastType;
                lastType = item.type;
                return (
                  <div key={`${item.type}-${item.id}`} className={showHeader ? "pt-4" : ""}>
                    {showHeader && (
                      <h3 className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground/50 pt-4 pb-1 px-1">
                        {item.type === 'person' ? 'Pessoas' : item.type === 'card' ? 'Cartões' : item.type === 'bank' ? 'Bancos' : 'Categorias'}
                      </h3>
                    )}
        <motion.div
          layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm"
                  data-testid={`connection-item-${item.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Icon */}
                      {item.type === "person" && (
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${item.relationship === "couple" ? "#ec4899" : item.relationship === "family" ? "#6366f1" : item.relationship === "friends" ? "#10b981" : "#6b7280"}15` }}
                        >
                          {(() => {
                            const Icon = getRelationshipIcon(item.relationship);
                            return <Icon className="w-6 h-6" style={{ color: item.relationship === "couple" ? "#ec4899" : item.relationship === "family" ? "#6366f1" : item.relationship === "friends" ? "#10b981" : "#6b7280" }} />;
                          })()}
                        </div>
                      )}
                      {item.type === "card" && (
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${item.color}15` }}
                        >
                          <CreditCard className="w-6 h-6" style={{ color: item.color }} />
                        </div>
                      )}
                      {item.type === "category" && (
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ 
                            backgroundColor: item.categoryType === "income" ? "#10b98115" : "#ef444415" 
                          }}
                        >
                          <Tag 
                            className="w-6 h-6" 
                            style={{ 
                              color: item.categoryType === "income" ? "#10b981" : "#ef4444" 
                            }} 
                          />
                        </div>
                      )}
                      {item.type === "bank" && (
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${item.color}15` }}
                        >
                          <Building2 className="w-6 h-6" style={{ color: item.color }} />
                        </div>
                      )}
                
                      {/* Info */}
                      <div>
                        <p className="font-semibold text-foreground">{item.name}</p>
                        {item.type === "person" && (
                          <p className="text-sm text-muted-foreground">
                            {item.nickname && `${item.nickname} • `}
                            {getRelationshipLabel(item)}
                          </p>
                        )}
                        {item.type === "card" && (
                          <p className="text-sm text-muted-foreground">
                            Fecha dia {item.closeDay} • Vence dia {item.dueDay}
                          </p>
                        )}
                        {item.type === "category" && (
                          <p className="text-sm text-muted-foreground">
                            Categoria de {item.categoryType === "income" ? "Receita" : "Despesa"}
                          </p>
                        )}
                        {item.type === "bank" && (
                          <p className="text-sm text-muted-foreground">Banco</p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item)}
                        className="w-9 h-9 rounded-lg hover:bg-slate-100"
                        data-testid={`edit-${item.id}`}
                      >
                        <Pencil className="w-4 h-4 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(item)}
                        className="w-9 h-9 rounded-lg hover:bg-red-50"
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  {/* Card limit info */}
                  {item.type === "card" && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Limite</span>
                        <span className="font-semibold text-foreground">
                          R$ {item.limit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  )}
                </motion.div>
      </div>
    );
  });
})()}
</AnimatePresence>
          </div>
        )}
      </div>

      {/* FAB Group */}
      <div className="fixed bottom-28 right-6 z-40">
        <AnimatePresence>
          {showFAB && (
            <>
              {/* Person FAB */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.1 }}
                className="absolute bottom-36 right-0"
              >
                <Button
                  onClick={() => { setShowPersonModal(true); setShowFAB(false); }}
                  className="w-12 h-12 rounded-full bg-pink-500 hover:bg-pink-600 shadow-lg shadow-pink-500/30"
                  data-testid="add-person-fab"
                >
                  <User className="w-5 h-5" />
                </Button>
                <span className="absolute right-14 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground bg-white px-3 py-1 rounded-lg shadow-md whitespace-nowrap">
                  Pessoa
                </span>
              </motion.div>

              {/* Card FAB */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ delay: 0.05 }}
                className="absolute bottom-24 right-0"
              >
                <Button
                  onClick={() => { setShowCardModal(true); setShowFAB(false); }}
                  className="w-12 h-12 rounded-full bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/30"
                  data-testid="add-card-fab"
                >
                  <CreditCard className="w-5 h-5" />
                </Button>
                <span className="absolute right-14 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground bg-white px-3 py-1 rounded-lg shadow-md whitespace-nowrap">
                  Cartão
                </span>
              </motion.div>

              {/* Bank FAB */}
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                className="absolute bottom-12 right-0"
              >
                <Button
                  onClick={() => { setShowBankModal(true); setShowFAB(false); }}
                  className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 shadow-lg shadow-emerald-500/30"
                  data-testid="add-bank-fab"
                >
                  <Building2 className="w-5 h-5" />
                </Button>
                <span className="absolute right-14 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground bg-white px-3 py-1 rounded-lg shadow-md whitespace-nowrap">
                  Banco
                </span>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* Main FAB */}
        <motion.div
          animate={{ rotate: showFAB ? 45 : 0 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Button
            onClick={() => setShowFAB(!showFAB)}
            className="w-14 h-14 rounded-full bg-primary hover:bg-primary/90 shadow-xl shadow-primary/30"
            data-testid="main-fab"
          >
            <Plus className="w-6 h-6" />
          </Button>
        </motion.div>
      </div>

      {/* Person Modal */}
      <Dialog open={showPersonModal} onOpenChange={(open) => { setShowPersonModal(open); if (!open) resetForms(); }}>
        <DialogContent className="w-full h-[100dvh] sm:h-auto sm:max-w-md sm:rounded-[24px] rounded-none flex flex-col border-none sm:border-solid">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingItem ? "Editar Pessoa" : "Nova Pessoa"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4 flex-1">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                placeholder="João, Maria, Ju..."
                value={personForm.name}
                onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })}
                className="rounded-xl h-12"
                data-testid="person-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Apelido</Label>
              <Input
                placeholder="Amor, Mãe, Cunhado, Sócio do churrasco"
                value={personForm.nickname}
                onChange={(e) => setPersonForm({ ...personForm, nickname: e.target.value })}
                className="rounded-xl h-12"
                data-testid="person-nickname-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Relacionamento *</Label>
              <Select
                value={personForm.relationship}
                onValueChange={(value) => setPersonForm({ ...personForm, relationship: value })}
              >
                <SelectTrigger className="rounded-xl h-12" data-testid="person-relationship-select">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {relationshipTypes.map((rel) => (
                    <SelectItem key={rel.id} value={rel.id}>
                      <div className="flex items-center gap-2">
                        <rel.icon className="w-4 h-4" />
                        {rel.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {personForm.relationship === "other" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-2"
              >
                <Label>Especifique *</Label>
                <Input
                  placeholder="Ex: Colega de trabalho, Vizinho..."
                  value={personForm.relationshipCustom}
                  onChange={(e) => setPersonForm({ ...personForm, relationshipCustom: e.target.value })}
                  className="rounded-xl h-12"
                  data-testid="person-relationship-custom"
                />
              </motion.div>
            )}
            <Button
              onClick={savePerson}
              className="w-full h-12 rounded-xl font-bold btn-primary mt-auto sm:mt-4"
              data-testid="save-person-btn"
            >
              {editingItem ? "Salvar Alterações" : "Adicionar Pessoa"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Card Modal */}
      <Dialog open={showCardModal} onOpenChange={(open) => { setShowCardModal(open); if (!open) resetForms(); }}>
        <DialogContent className="w-full h-[100dvh] sm:h-auto sm:max-w-md sm:rounded-[24px] rounded-none flex flex-col border-none sm:border-solid">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingItem ? "Editar Cartão" : "Novo Cartão"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4 flex-1">
            <div className="space-y-2">
              <Label>Nome do Cartão *</Label>
              <Input
                placeholder="Ex: Nubank, Itaú..."
                value={cardForm.name}
                onChange={(e) => setCardForm({ ...cardForm, name: e.target.value })}
                className="rounded-xl h-12"
                data-testid="card-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Limite (Opcional)</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">R$</span>
                <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={cardForm.limit ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(parseFloat(cardForm.limit)) : ""}
                          onChange={(e) => {
                            const cleanValue = e.target.value.replace(/\D/g, "");
                            const floatValue = parseFloat(cleanValue) / 100;
                            setCardForm({ ...cardForm, limit: isNaN(floatValue) ? "0" : floatValue.toString() });
                          }}
                          className="rounded-xl h-12 pl-12"
                        />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Dia de Fechamento *</Label>
                <Input
                  type="number"
                  placeholder="8"
                  min="1"
                  max="31"
                  value={cardForm.closeDay}
                  onChange={(e) => setCardForm({ ...cardForm, closeDay: e.target.value })}
                  className="rounded-xl h-12"
                  data-testid="card-close-day-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Dia de Vencimento *</Label>
                <Input
                  type="number"
                  placeholder="15"
                  min="1"
                  max="31"
                  value={cardForm.dueDay}
                  onChange={(e) => setCardForm({ ...cardForm, dueDay: e.target.value })}
                  className="rounded-xl h-12"
                  data-testid="card-due-day-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="grid grid-cols-6 gap-2">
                {colorPalette.map((color) => (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => setCardForm({ ...cardForm, color: color.hex })}
                    className={`w-10 h-10 rounded-xl transition-all ${
                      cardForm.color === color.hex ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : ""
                    }`}
                    style={{ backgroundColor: color.hex }}
                    data-testid={`card-color-${color.name}`}
                  />
                ))}
              </div>
            </div>
            <Button
              onClick={saveCard}
              className="w-full h-12 rounded-xl font-bold btn-primary mt-auto sm:mt-4"
              data-testid="save-card-btn"
            >
              {editingItem ? "Salvar Alterações" : "Adicionar Cartão"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Modal */}
      <Dialog open={showBankModal} onOpenChange={(open) => { setShowBankModal(open); if (!open) resetForms(); }}>
        <DialogContent className="w-full h-[100dvh] sm:h-auto sm:max-w-md sm:rounded-[24px] rounded-none flex flex-col border-none sm:border-solid">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingItem ? "Editar Banco" : "Novo Banco"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4 flex-1">
            <div className="space-y-2">
              <Label>Nome do Banco *</Label>
              <Input
                placeholder="Ex: Nubank, Itaú, BB..."
                value={bankForm.name}
                onChange={(e) => setBankForm({ ...bankForm, name: e.target.value })}
                className="rounded-xl h-12"
                data-testid="bank-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="grid grid-cols-6 gap-2">
                {colorPalette.map((color) => (
                  <button
                    key={color.hex}
                    type="button"
                    onClick={() => setBankForm({ ...bankForm, color: color.hex })}
                    className={`w-10 h-10 rounded-xl transition-all ${
                      bankForm.color === color.hex ? "ring-2 ring-offset-2 ring-slate-400 scale-110" : ""
                    }`}
                    style={{ backgroundColor: color.hex }}
                    data-testid={`bank-color-${color.name}`}
                  />
                ))}
              </div>
            </div>
            <Button
              onClick={saveBank}
              className="w-full h-12 rounded-xl font-bold btn-primary mt-auto sm:mt-4"
              data-testid="save-bank-btn"
            >
              {editingItem ? "Salvar Alterações" : "Adicionar Banco"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Modal */}
<Dialog open={showCategoryModal} onOpenChange={(open) => { setShowCategoryModal(open); if (!open) resetForms(); }}>
  <DialogContent className="w-full h-[100dvh] sm:h-auto sm:max-w-md sm:rounded-[24px] rounded-none flex flex-col border-none sm:border-solid">
    <DialogHeader>
      <DialogTitle className="text-xl font-bold">
        {editingItem ? "Editar Categoria" : "Nova Categoria"}
      </DialogTitle>
    </DialogHeader>
    <div className="space-y-4 pt-4 flex-1">
      <div className="space-y-2">
        <Label>Nome da Categoria *</Label>
        <Input
          placeholder="Ex: Terapia, loja x , Rolês Aleatórios,..."
          value={categoryForm.name}
          onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
          className="rounded-xl h-12"
        />
      </div>
      <div className="space-y-2">
        <Label>Tipo *</Label>
        <Select
          value={categoryForm.categoryType}
          onValueChange={(val) => setCategoryForm({ ...categoryForm, categoryType: val })}
        >
          <SelectTrigger className="rounded-xl h-12">
            <SelectValue placeholder="Selecione o tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="expense">Despesa</SelectItem>
            <SelectItem value="income">Receita</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button
        onClick={saveCategory}
        className="w-full h-12 rounded-xl font-bold btn-primary mt-auto sm:mt-4"
        disabled={isSaving}
      >
        {isSaving ? <Loader2 className="animate-spin" /> : editingItem ? "Salvar Alterações" : "Adicionar Categoria"}
      </Button>
    </div>
  </DialogContent>
</Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="rounded-[24px] mx-4">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir "{deleteConfirm?.name}"? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-xl bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
